import { BehaviorSubject } from 'rxjs';
import { Account, Product } from '../types';
import { getSelectedAccountIdsForUrl, getSelectedAccountsSubject } from './User';
import { addError } from './Error';
import { getPriceBenchmarks, getPriceBenchmarkById } from '../Api/Benchmark/benchmarkApi';

import { BenchmarkStatus } from './BenchmarkStatus';
export { BenchmarkStatus };

export interface PriceBenchMark {
  id: string;
  origin: boolean;
  assetId: string;
  address: string;
  product: Product;
  accountName: string;
  supplier: Account;
  speed: string;
  mrc: number;
  priceToMarket: number;
  priceToSupplier: number;
  priceToSupplierName: string;
  priceToLowest: number;
  priceToLowestName: string;
  priceToHighest: number;
  priceToHighestName: string;
  term: string;
  contractEndDate: string;
  startDate?: string;
  benchmarkStatus?: BenchmarkStatus;
  sfId?: string;
  speed_c?: string;
  accessspeed_c?: string;
  port_speed_c?: string;
  bandwidth_mbps_c?: string;
  billing_commencement_date_c?: string;
  completiondate?: string;
  shippingpostalcode?: string;
  supplier_name?: string;
}
export const defaultPriceBenchMark: PriceBenchMark[] = [];
const subject = new BehaviorSubject<PriceBenchMark[]>(defaultPriceBenchMark);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('benchmark', message);

export const getPriceBenchmarkSubject = () => subject;
export const getPriceBenchmarkLoadingSubject = () => loadingSubject;
export const clearPriceBenchmarks = () => subject.next(defaultPriceBenchMark);

// Clear stale benchmark data when the account scope changes so the UI never
// shows results for the previous account. The actual re-fetch is handled by
// the callers that trigger the account change (AccountDropdown, AccountModal).
// Guard against partial User mocks in test environments where
// getSelectedAccountsSubject may not be available or may return undefined.
if (typeof getSelectedAccountsSubject === 'function') {
  const accountSubject = getSelectedAccountsSubject();
  if (accountSubject) {
    let prevAccountKey: string | null = null;
    accountSubject.subscribe((accounts) => {
      const key = (accounts || [])
        .map((a) => a.id)
        .sort()
        .join(',');
      if (key === prevAccountKey) return;
      const isFirst = prevAccountKey === null;
      prevAccountKey = key;
      if (isFirst) return;
      clearPriceBenchmarks();
    });
  }
}

let currentAbortController: AbortController | null = null;
let inflightFetch: Promise<void> | null = null;

export const fetchPriceBenchmarks = async (forceRefresh = false) => {
  if (!forceRefresh && subject.getValue().length > 0) return;
  if (!forceRefresh && inflightFetch) return inflightFetch;
  if (!forceRefresh && loadingSubject.getValue()) return;

  currentAbortController?.abort();
  const controller = new AbortController();
  currentAbortController = controller;

  loadingSubject.next(true);
  inflightFetch = getPriceBenchmarks(getSelectedAccountIdsForUrl(), controller.signal).then(({ data, error }) => {
    inflightFetch = null;
    if (controller.signal.aborted) return;

    if (data) {
      emitErrorState();
      subject.next(data);
    }

    if (error) {
      emitErrorState(error.message);
      subject.next(defaultPriceBenchMark);
    }
    loadingSubject.next(false);
  });

  return inflightFetch;
};

export const refreshPriceBenchmarkForAsset = async (assetId: string): Promise<void> => {
  const current = subject.getValue();
  const benchmark = current.find((b) => b.assetId === assetId);
  if (!benchmark) return;
  const { data } = await getPriceBenchmarkById(benchmark.id, getSelectedAccountIdsForUrl());
  if (data) {
    subject.next(current.map((b) => (b.assetId === assetId ? data : b)));
  }
};
